import { Form, Button, useModal, ButtonVariant, ButtonColor } from '@ampol/design-system';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import CardUsageOptions from '../card-usage-options/card-usage-options';
import * as yup from 'yup';

import './card-usage-options-form.scss';
import {
  getAddEditCardRequest,
  getAddEditFavouriteRequest,
  getCardUsageOptionsRequest,
  getCardUsageOptionsSchema,
  getEditCardDetails,
  getFavouritesDetails,
  getFleetLimiterCodesObj,
  vasClassifierMapping,
  getRequiredVasSchema,
  getRequiredVasFieldsRequest,
  isEvProductSelected,
  cardHasRfid,
  isBulkReissueRequired,
} from 'features/cards/helpers/add-edit-card-helper';
import { useSelector, useDispatch } from 'react-redux';
import { selectCardUsageOptionsFormContext } from 'selectors/cardUsageOptionsFormSelectors';
import {
  setAccountDefaultButton,
  setAddEditFavouritesButtons,
  setCardUsageOptionsButtons,
  setEditFavouritesButtons,
} from 'actions/modalMetaActions';
import {
  createCardOptionsAction,
  getCardOptionsAction,
  getCardOptionsReset,
  getVehicleTypesAction,
  updateCardOptionsAction,
} from 'actions/storeActions';
import { getCardAccountDefaultRequest, setCardOptions, editCardDetails } from 'actions/cardActions';
import { fetchClient } from 'utilities/fetchClient';
import { AUSSIE_STATES } from 'utilities/constants';
import clsx from 'clsx';
import { trackCustomEvent } from 'utilities/app-insights';
import Env from 'Env';
import {
  capDailyLimitToCreditLimit,
  capDailyLimitToMaximumOption,
  filterDailyDollarLimitsByCreditLimit,
  filterRestrictedDailyDollarLimits,
  getEffectiveCreditLimit,
} from 'features/cards/helpers/card-limit-utilities';

interface CardUsageOptionsFormProps {
  formId: string;
  cardDetails?: any;
  readOnly?: boolean;
  contractNumber?: string | string[];
  cardsList?: string[];
  cardsData?: any[];
  onClose?: () => void;
  onEvReissueComplete?: () => void;
}

interface PendingEvRequest {
  request: Record<string, any>;
  cardsWithoutRfid: string[];
  fullCardDetailsMap: Record<string, any>;
}

/**
 * Form used for Accounts modal Card Favourites sidemenu, Cards action menu modal, Cards modal Card Usage Options sidemenu
 * This is not used in the AddEdit form
 */
const CardUsageOptionsForm = ({
  formId,
  cardDetails,
  readOnly,
  contractNumber,
  cardsList = [],
  cardsData,
  onClose,
  onEvReissueComplete,
}: CardUsageOptionsFormProps) => {
  const dispatch = useDispatch();
  const allAccounts = useSelector((state: any) => state.accountsStore?.allAccounts ?? []);

  const {
    assignedGrandfatherClientNumber,
    accountDefaults,
    accountDefaultsLoading,
    cardOptionDetails,
    cardOptions,
    cardOptionsLoading,
    vehicleTypes,
    canReadWrite,
    createCardOptionSuccess,
    updateCardOptionSuccess,
    loggedInUser,
    assignedAccountNumber,
  } = useSelector(selectCardUsageOptionsFormContext);

  const [isValid, setIsValid] = useState(false);
  // pendingEvRequest (state) drives the modal content re-render on each cycle.
  // pendingEvRequestRef (ref) is read inside the useModal CONFIRM action closure,
  // which closes over a stale state value — the ref always gives the latest value.
  const [pendingEvRequest, setPendingEvRequest] = useState<PendingEvRequest | null>(null);
  const pendingEvRequestRef = useRef<PendingEvRequest | null>(null);
  const pendingBulkRequestRef = useRef<Record<string, any> | null>(null);

  const fleetLimitersRef = useRef<any[] | undefined>(undefined);
  const cardOptionsRef = useRef<any[]>(cardOptions);
  const accountDefaultsRef = useRef<any>(undefined);
  const isMounted = useRef(false);

  const [defaultValues, setDefaultValues] = useState<Record<string, any>>({});
  const [validationSchema, setValidationSchema] = useState(
    cardDetails ? getCardUsageOptionsSchema(cardDetails?.fleetLimiters?.map((option: any) => option.code)) : {}
  );
  const [favMode, setFavMode] = useState('');

  const accountFavouritesData = useMemo(
    () =>
      cardOptions.map((item: any) => ({
        label: item.favouriteName,
        value: item.id,
      })),
    [cardOptions]
  );

  const isAddEditingFavourite = favMode !== '';
  const accountCreditLimit = getEffectiveCreditLimit(allAccounts, contractNumber ?? cardDetails?.contractNumber);
  const dailyDollarLimitsOptions = cardDetails ? cardDetails?.dailyDollarLimits : accountDefaults?.dailyDollarLimits;
  const shouldFilterDailyLimits = typeof Env.getIsCardLimitsReadOnly === 'function' && Env.getIsCardLimitsReadOnly();
  const shouldRestrictDailyLimits = formId === 'card-usage-options-modal';
  const allowedDailyDollarLimits = shouldRestrictDailyLimits
    ? filterRestrictedDailyDollarLimits(dailyDollarLimitsOptions)
    : dailyDollarLimitsOptions;
  const availableDailyDollarLimits = shouldFilterDailyLimits
    ? filterDailyDollarLimitsByCreditLimit(allowedDailyDollarLimits, accountCreditLimit)
    : allowedDailyDollarLimits;

  const handleStateChange = ({ isValid }: { isValid: boolean }) => {
    setIsValid(isValid);
  };

  // ANTI-PATTERN FIX: Extract helper for determining allProducts state
  const calculateAllProductsState = useCallback((values: Record<string, any>, fleetLimiters: any[] | undefined) => {
    if (!fleetLimiters) return values;

    const availableProducts = fleetLimiters.filter((option) => option.available);
    if (!availableProducts.length) return values;

    const selectedProducts = availableProducts.filter((option) => values[option.code]);
    const allSelected = availableProducts.length === selectedProducts.length;

    return { ...values, allProducts: allSelected };
  }, []);

  const handleChange = (values: Record<string, any>, name?: string) => {
    if (!name) return;
    if (name === 'allProducts') {
      setDefaultValues({
        ...values,
        ...fleetLimitersRef?.current?.reduce(
          (acc: Record<string, any>, option: any) => ({
            ...acc,
            [option.code]: option.available ? values.allProducts : false,
          }),
          {}
        ),
      });
    } else if (name === 'accountFavorites') {
      if (formId === 'card-usage-options-account-detail' && canReadWrite) {
        dispatch(
          setEditFavouritesButtons({
            onClick: handleEditFavouriteClick,
          })
        );
      }
      const favSelected = cardOptionsRef.current.find((obj: any) => obj.id === values.accountFavorites);
      const favouriteDetails = getFavouritesDetails(
        favSelected,
        fleetLimitersRef.current,
        cardDetails ? cardDetails?.accountDefault?.vasRsaAccountCode : accountDefaultsRef.current?.vasRsaAccountCode,
        cardDetails ? cardDetails?.accountDefault?.vasSnrAccountCode : accountDefaultsRef.current?.vasSnrAccountCode
      );
      setDefaultValues({
        ...values,
        ...favouriteDetails,
        dailyLimit: capDailyLimitToMaximumOption(
          shouldFilterDailyLimits
            ? capDailyLimitToCreditLimit(favouriteDetails.dailyLimit, allowedDailyDollarLimits, accountCreditLimit)
            : favouriteDetails.dailyLimit,
          availableDailyDollarLimits
        ),
      });
      trackCustomEvent(
        'Select Card Favourite Dropdown',
        loggedInUser,
        assignedAccountNumber,
        'Selected card favourite from dropdown.',
        { selectedAccount: contractNumber }
      );
    } else {
      // ANTI-PATTERN FIX: Use extracted helper for cleaner logic
      const updatedValues = calculateAllProductsState(values, fleetLimitersRef.current);
      setDefaultValues(updatedValues);
    }
  };

  // EV reissue confirmation modal — uses useModal directly (not via ConfirmExitModal component)
  // so the design system always has the latest content and action callbacks on each render.
  // Follows the same pattern as useMultiEditConfirmModal.tsx.
  const evReissueModal = useModal({
    id: 'ev-reissue-confirm-modal',
    title: 'Edit Card',
    variant: 'double',
    color: ButtonColor.Blue,
    hideCloseButton: true,
    content: pendingEvRequest ? (
      <>
        <p>
          You have made changes that will cause a reissue to <strong>{pendingEvRequest.cardsWithoutRfid.length}</strong>{' '}
          of the <strong>{cardsList.length}</strong> cards. Are you sure you want to continue?
        </p>
        <ul className='max-h-[180px] overflow-y-auto mt-3 p-0 list-none'>
          {pendingEvRequest.cardsWithoutRfid.map((cardNumber) => (
            <li key={cardNumber} className='py-2 border-b border-gray-200'>
              {cardNumber}
            </li>
          ))}
        </ul>
      </>
    ) : (
      <></>
    ),
    actions: [
      {
        label: 'CONFIRM',
        variant: ButtonVariant.Primary,
        onClick: () => {
          const pending = pendingEvRequestRef.current;
          if (!pending) return;
          const { request, cardsWithoutRfid, fullCardDetailsMap } = pending;

          cardsWithoutRfid.forEach((cardNumber) => {
            const cardData =
              fullCardDetailsMap?.[cardNumber] ?? cardsData?.find((c: any) => c.cardNumber === cardNumber);
            dispatch(
              editCardDetails(
                getAddEditCardRequest(
                  {
                    ...getEditCardDetails(cardData),
                    ...getFleetLimiterCodesObj(cardData?.fleetLimiters),
                  },
                  cardData?.fleetLimiters,
                  assignedGrandfatherClientNumber
                ),
                assignedGrandfatherClientNumber
              )
            );
          });

          dispatch(
            setCardOptions(
              { cards: cardsList.map((card) => ({ contractIdentifier: card, ...request })) },
              assignedGrandfatherClientNumber
            )
          );

          trackCustomEvent(
            'Submit Update Card Usage Options with EV Reissue',
            loggedInUser,
            assignedAccountNumber,
            'Confirmed EV reissue and applied usage options to cards.',
            { selectedAccount: contractNumber }
          );

          setPendingEvRequest(null);
          pendingEvRequestRef.current = null;
          // Signal that an EV reissue was confirmed so CardUsageOptionsModal can
          // watch for the saga to complete before showing the acknowledge modal.
          // close() (not clear()) so formModal stays open while the saga runs.
          onEvReissueComplete?.();
          evReissueModal.close();
        },
      },
      {
        label: 'CANCEL',
        onClick: () => {
          setPendingEvRequest(null);
          pendingEvRequestRef.current = null;
          evReissueModal.close();
        },
      },
    ],
  });

  const bulkReissueModal = useModal({
    id: 'bulk-card-reissue-modal',
    title: 'Edit Cards',
    variant: 'double',
    color: ButtonColor.Blue,
    hideCloseButton: true,
    content: (
      <div>
        <p>You have made changes that will cause a reissue for the {cardsList.length} cards:</p>
        <ul className='max-h-[200px] overflow-y-auto mt-3'>
          {cardsList.map((card) => (
            <li key={card}>{card}</li>
          ))}
        </ul>
      </div>
    ),
    actions: [
      {
        label: 'CONFIRM',
        variant: ButtonVariant.Primary,
        onClick: () => {
          const request = pendingBulkRequestRef.current;
          if (!request) return;

          dispatch(
            setCardOptions(
              {
                cards: cardsList.map((card) => ({
                  contractIdentifier: card,
                  ...request,
                })),
              },
              assignedGrandfatherClientNumber
            )
          );

          pendingBulkRequestRef.current = null;
          bulkReissueModal.close();
          onClose?.();
        },
      },
      {
        label: 'CANCEL',
        onClick: () => {
          pendingBulkRequestRef.current = null;
          bulkReissueModal.close();
        },
      },
    ],
  });

  const handleSubmit = async (values: Record<string, any>) => {
    if (favMode === 'add') {
      const request = getAddEditFavouriteRequest(values, contractNumber, fleetLimitersRef.current ?? []);
      dispatch(createCardOptionsAction(request, assignedGrandfatherClientNumber));
      handleAddEditFavouriteCancelClick();
      trackCustomEvent(
        'Submit Add Card Favourite',
        loggedInUser,
        assignedAccountNumber,
        'Clicked Submit to add new Card Favourite.',
        { selectedAccount: contractNumber }
      );
    } else if (favMode === 'edit') {
      const request = getAddEditFavouriteRequest(
        values,
        contractNumber,
        fleetLimitersRef.current ?? [],
        cardOptionDetails.id
      );
      dispatch(updateCardOptionsAction(request, assignedGrandfatherClientNumber));
      handleAddEditFavouriteCancelClick();
      trackCustomEvent(
        'Submit Edit Card Favourite',
        loggedInUser,
        assignedAccountNumber,
        'Clicked Submit to edit a Card Favourite.',
        { selectedAccount: contractNumber }
      );
    } else if (formId === 'card-usage-options-modal') {
      const request = {
        ...getCardUsageOptionsRequest(values, fleetLimitersRef.current ?? []),
        ...getRequiredVasFieldsRequest(values),
      };

      // AC2: Check RFID when EV product is selected (only when VITE_EV_WAY4 flag is enabled)
      if (Env.getIsEvWay4Enabled() && isEvProductSelected(values)) {
        // Fetch full card details for ALL selected cards so we can read hasRfid.
        // The list endpoint does not include hasRfid, so we must use the detail endpoint.
        // Uses Promise.allSettled so a failed fetch falls back to list-view data.
        const results = await Promise.allSettled(
          cardsList.map((cardNumber) =>
            fetchClient({
              url: `cards-api/v1/cards/${cardNumber}/detail`,
              method: 'GET',
              body: null,
              apiServer: Env.getApimBaseUrl(),
              headers: { 'Ocp-Apim-Subscription-Key': Env.getApimKey() },
              queryParams: { grandParentAccountNumber: assignedGrandfatherClientNumber },
            })
          )
        );
        const fullCardDetailsMap = Object.fromEntries(
          cardsList.map((cardNumber, i) => [
            cardNumber,
            results[i].status === 'fulfilled'
              ? results[i].value
              : cardsData?.find((c: any) => c.cardNumber === cardNumber),
          ])
        );

        const cardsWithoutRfid = cardsList.filter((cardNumber) => !cardHasRfid(fullCardDetailsMap[cardNumber]));

        if (cardsWithoutRfid.length > 0) {
          // Show confirmation modal
          setPendingEvRequest({ request, cardsWithoutRfid, fullCardDetailsMap });
          pendingEvRequestRef.current = { request, cardsWithoutRfid, fullCardDetailsMap };
          evReissueModal.open();
          return;
        }
      }

      // Generic bulk-reissue guard for non-EV reissue-triggering field edits.
      // EV flow above remains highest priority because it has card-level RFID checks.
      const selectedOriginalCards = (cardsData ?? []).filter((card: any) => cardsList.includes(card.cardNumber));
      const requiresBulkReissue =
        selectedOriginalCards.length > 0 && isBulkReissueRequired(selectedOriginalCards, values);

      if (requiresBulkReissue) {
        pendingBulkRequestRef.current = request;
        bulkReissueModal.open();
        return;
      }

      dispatch(
        setCardOptions(
          {
            cards: cardsList.map((card) => {
              return {
                contractIdentifier: card,
                ...request,
              };
            }),
          },
          assignedGrandfatherClientNumber
        )
      );

      trackCustomEvent(
        'Submit Update Card Usage Options',
        loggedInUser,
        assignedAccountNumber,
        'Clicked Apply to submit Card Usage Option updates.',
        { selectedAccount: contractNumber }
      );

      onClose?.();
    }
  };

  const handleAddEditFavouriteCancelClick = useCallback(() => {
    setFavMode('');
    dispatch(setAccountDefaultButton());
    setDefaultValues({
      accountFavorites: '',
    });
    (document.getElementById(formId) as HTMLFormElement | null)?.reset();
  }, [formId, dispatch]);

  // ANTI-PATTERN FIX: Update button state when form validity changes
  // Consolidates logic previously duplicated in useEffect and handleStateChange
  useEffect(() => {
    if (isAddEditingFavourite && formId === 'card-usage-options-account-detail') {
      dispatch(
        setAddEditFavouritesButtons({
          formId: formId,
          inactive: !isValid,
          onCancelClick: handleAddEditFavouriteCancelClick,
        })
      );
    }
  }, [isValid, isAddEditingFavourite, formId, dispatch, handleAddEditFavouriteCancelClick]);

  const handleAddNewFavouriteClick = () => {
    setFavMode('add');
    (document.getElementById(formId) as HTMLFormElement | null)?.reset();
    dispatch(
      setAddEditFavouritesButtons({
        formId: formId,
        inactive: true,
        onCancelClick: handleAddEditFavouriteCancelClick,
      })
    );

    setDefaultValues({
      monthlyLimit: accountDefaults?.monthlyLimit ? accountDefaults.monthlyLimit.toString() : null,
      dailyLimit: accountDefaults?.dailyLimit ? accountDefaults.dailyLimit.toString() : null,
      allProducts: accountDefaults?.fleetLimiters?.every(
        (item: any) => item.available === false || item.selected === true
      ),
      ...getFleetLimiterCodesObj(accountDefaults?.fleetLimiters),
      ...(accountDefaults?.vasRsaAccountCode !== 'NC' && {
        roadsideAssist: true,
        vehicleType: vasClassifierMapping(accountDefaults?.vasRsaAccountCode),
      }),
    });

    setValidationSchema(
      getCardUsageOptionsSchema(
        fleetLimitersRef.current?.map((option: any) => option.code),
        cardOptionsRef.current?.map((obj: any) => obj.favouriteName)
      )
    );

    trackCustomEvent(
      'Click Add Card Favourite',
      loggedInUser,
      assignedAccountNumber,
      'Clicked Add New Favourite button.',
      { selectedAccount: contractNumber }
    );
  };

  const handleEditFavouriteClick = () => {
    // Use current defaultValues state directly instead of ref
    const formValues = defaultValues;

    setFavMode('edit');

    setValidationSchema(
      getCardUsageOptionsSchema(
        fleetLimitersRef.current?.map((option: any) => option.code),
        cardOptionsRef.current
          ?.filter((obj: any) => obj.id !== formValues.accountFavorites)
          .map((obj: any) => obj.favouriteName)
      )
    );

    setDefaultValues({
      ...formValues,
      cardFavoriteName: cardOptionsRef.current.find((item: any) => item.id === formValues.accountFavorites)
        .favouriteName,
    });

    trackCustomEvent(
      'Click Edit Card Favourite',
      loggedInUser,
      assignedAccountNumber,
      'Clicked Edit button to update card favourite details.',
      { selectedAccount: contractNumber }
    );
  };

  useEffect(() => {
    if (contractNumber) {
      dispatch(getCardOptionsReset());
      dispatch(getCardOptionsAction(contractNumber, assignedGrandfatherClientNumber));
    }
    // contractNumber is an array prop - including it would cause a new reference on every render
    // and trigger this effect in an infinite loop. It is stable for the lifetime of the modal.
  }, [createCardOptionSuccess, updateCardOptionSuccess]);

  useEffect(() => {
    if (contractNumber) {
      dispatch(
        getCardAccountDefaultRequest({
          contractNumber: contractNumber,
          grandParentAccountNumber: assignedGrandfatherClientNumber,
        })
      );
    } else {
      setDefaultValues({
        ...getEditCardDetails(cardDetails),
        ...cardDetails?.fleetLimiters?.reduce(
          (acc: Record<string, any>, option: any) => ({ ...acc, [option.code]: option.selected }),
          {}
        ),
      });
    }

    return () => {
      dispatch(setAccountDefaultButton());
    };
    // contractNumber is an array prop - including it would cause a new reference on every render
    // and trigger this effect in an infinite loop causing 429 Too Many Requests.
    // cardDetails changing (switching cards) is the correct signal to re-fetch defaults.
  }, [cardDetails]);

  // Separate effect for vehicle types: only fetch once on mount if not yet loaded.
  // Intentionally not including vehicleTypes.length so that loading vehicle types
  // does not re-trigger getCardAccountDefaultRequest (which caused 429 Too Many Requests).
  useEffect(() => {
    if (contractNumber && vehicleTypes.length === 0) {
      dispatch(getVehicleTypesAction());
    }
  }, []);

  // ANTI-PATTERN FIX: Simplified ref sync - removed isMounted pattern
  useEffect(() => {
    if (cardOptions) {
      cardOptionsRef.current = cardOptions;
    }
  }, [cardOptions]);

  // ANTI-PATTERN FIX: Split complex effect into focused responsibilities
  useEffect(() => {
    // isMounted must be set unconditionally (before any early return) so that it is true
    // by the time accountDefaults first contains real data, matching the original behaviour.
    const shouldSkip = !accountDefaults || Object.keys(accountDefaults).length === 0 || !isMounted.current;
    isMounted.current = true;
    if (shouldSkip) return;

    // Update refs
    fleetLimitersRef.current = accountDefaults?.fleetLimiters;
    accountDefaultsRef.current = accountDefaults;

    // Set initial form values
    setDefaultValues({
      ...accountDefaults?.fleetLimiters?.reduce(
        (acc: Record<string, any>, option: any) => ({ ...acc, [option.code]: false }),
        {}
      ),
    });

    // Build validation schema
    const cardUsageOptionsSchema = getCardUsageOptionsSchema(
      accountDefaults?.fleetLimiters?.map((option: any) => option.code)
    );

    const isCardUsageOptionsModal = formId === 'card-usage-options-modal';
    const schema = isCardUsageOptionsModal
      ? cardUsageOptionsSchema.concat(yup.object().shape({ ...getRequiredVasSchema() }))
      : cardUsageOptionsSchema;

    setValidationSchema(schema);

    // Initialize modal buttons
    if (isCardUsageOptionsModal) {
      dispatch(setCardUsageOptionsButtons({ formId: formId }));
    }
  }, [accountDefaults, formId, dispatch]);

  // Update validation schema for account detail form when options change
  useEffect(() => {
    if (formId !== 'card-usage-options-account-detail') return;
    if (!accountDefaults || Object.keys(accountDefaults).length === 0) return;
    if (!cardOptions) return;

    setValidationSchema(
      getCardUsageOptionsSchema(
        accountDefaults?.fleetLimiters?.map((option: any) => option.code),
        cardOptions.map((obj: any) => obj.favouriteName)
      )
    );
  }, [accountDefaults, cardOptions, formId]);

  return (
    <div
      className={clsx('card-usage-options-form', {
        'card-usage-options-form--modal': formId === 'card-usage-options-modal',
      })}
    >
      <Form
        id={formId}
        schema={validationSchema}
        defaultValues={defaultValues}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onStateChange={handleStateChange}
      >
        <div className='card-usage-options-form__section'>
          {contractNumber && !isAddEditingFavourite && (
            <Form.DropdownSearch
              label={'Select Favourites'}
              name={'accountFavorites'}
              data={accountFavouritesData}
              allowClear={false}
              loading={cardOptionsLoading}
            />
          )}
          {isAddEditingFavourite && (
            <Form.Input
              label='Enter Card Favourite Name'
              name='cardFavoriteName'
              id='cardFavoriteName'
              className={'card-usage-options-form__uppercase'}
            />
          )}
          {!isAddEditingFavourite && formId === 'card-usage-options-account-detail' && canReadWrite && (
            <Button
              label={'Add new favourite'}
              variant={'primary'}
              color='blue'
              onClick={!accountDefaultsLoading ? handleAddNewFavouriteClick : undefined}
              className='card-usage-options-form__button'
              inactive={accountDefaultsLoading}
            />
          )}
          <CardUsageOptions
            fleetLimiterOptions={cardDetails ? cardDetails?.fleetLimiters : accountDefaults?.fleetLimiters}
            monthlyDollarLimitsOptions={
              cardDetails ? cardDetails?.monthlyDollarLimits : accountDefaults?.monthlyDollarLimits
            }
            dailyDollarLimitsOptions={allowedDailyDollarLimits}
            accountCreditLimit={accountCreditLimit}
            vehicleTypesOptions={vehicleTypes}
            vasRsaAccountCode={
              cardDetails ? cardDetails?.accountDefault?.vasRsaAccountCode : accountDefaults?.vasRsaAccountCode
            }
            vasSnrAccountCode={
              cardDetails ? cardDetails?.accountDefault?.vasSnrAccountCode : accountDefaults?.vasSnrAccountCode
            }
            readOnly={readOnly && !isAddEditingFavourite}
            formValues={defaultValues}
            servicesAndRepairsFee={
              cardDetails
                ? cardDetails?.accountDefault?.vasServicesAndRepairsFee
                : accountDefaults?.vasServicesAndRepairsFee
            }
            roadSideAssistFeeHeavy={
              cardDetails
                ? cardDetails?.accountDefault?.vasRoadSideAssistFeeHeavy
                : accountDefaults?.vasRoadSideAssistFeeHeavy
            }
            roadSideAssistFeePassenger={
              cardDetails
                ? cardDetails?.accountDefault?.vasRoadSideAssistFeePassenger
                : accountDefaults?.vasRoadSideAssistFeePassenger
            }
            roadSideAssistFeeTruck={
              cardDetails
                ? cardDetails?.accountDefault?.vasRoadSideAssistFeeTruck
                : accountDefaults?.vasRoadSideAssistFeeTruck
            }
            vehicleType={defaultValues?.vehicleType}
            vehicleTypes={vehicleTypes}
            isCardProcessing={
              cardsData
                ? cardsData.some((c: any) => cardsList?.includes(c.cardNumber) && c.status === 'Processing')
                : cardDetails?.cardStatus === 'Processing'
            }
          />

          {formId === 'card-usage-options-modal' && (
            <Form.Condition
              fieldName='roadsideAssist'
              condition={() => defaultValues?.roadsideAssist || defaultValues?.serviceAndRepairs}
            >
              <Form.Input
                label={'Vehicle Registration'}
                name={'registrationNumber'}
                id={'registrationNumber'}
                className={'card-usage-options-form__uppercase'}
              />
              <Form.DropdownSearch
                label={'Registration State'}
                name={'registrationState'}
                type={'dropdown'}
                data={AUSSIE_STATES}
                placeholder={'Select Registration State'}
              />
              <div className={'card-usage-options-form__full'}>
                <Form.Input
                  label={'Vehicle Description'}
                  name={'vehicleDescription'}
                  id={'vehicleDescription'}
                  className={'card-usage-options-form__uppercase'}
                />
              </div>
            </Form.Condition>
          )}
        </div>
      </Form>
    </div>
  );
};

export default CardUsageOptionsForm;
